import React, { useState, useEffect } from 'react';
import { apiClient } from '../services/api';
import { Case } from '../hooks/useCasesApi';
import SubmissionModal from './SubmissionModal';
import { FiFile, FiPlayCircle, FiLoader, FiAlertTriangle, FiCheckCircle, FiXCircle, FiMinusCircle } from 'react-icons/fi';
import AuditAnimation from './auditAnimation'

// Define interfaces for the data we'll be working with
interface Document {
  id: string;
  fileName: string;
  fileSize: number;
  fileUrl: string;
  uploadedAt: Date;
  ocrStatus: string;
  ocrData?: any;
  documentType?: string;
}

interface Finding {
  documentType: string;
  field: string;
  intakeValue: string;
  ocrValue: string;
  readibility: string;
  status: 'match' | 'mismatch' | 'near_miss';
}

interface AuditResult {
  totals: {
    compared: number;
    matches: number;
    mismatches: number;
    near_misses: number;
  };
  findings: Finding[];
}

export default function Audit() {
  const [cases, setCases] = useState<Case[]>([]);
  const [selectedCase, setSelectedCase] = useState<string | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loadingCases, setLoadingCases] = useState(true);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [auditing, setAuditing] = useState(false);
  const [auditResult, setAuditResult] = useState<AuditResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<any | null>(null);
  const [submissionModalOpen, setSubmissionModalOpen] = useState(false);

  // Fetch cases on component mount
  useEffect(() => {
    const fetchCases = async () => {
      try {
        setLoadingCases(true);
        const data = await apiClient.getCases();
        setCases(data);
        if (data.length > 0) {
          setSelectedCase(data[0].id);
        }
      } catch (err) {
        setError('Failed to fetch cases. Please try again.');
      } finally {
        setLoadingCases(false);
      }
    };
    fetchCases();
  }, []);

  const groupFindingsByDocument = (findings: Finding[]) => {
  const order = ['passport'];
  const map: Record<string, Finding[]> = {};
  findings.forEach(f => {
    const dt = String((f as any).documentType || (f as any).document_type || 'general').toLowerCase();
    if (!map[dt]) map[dt] = [];
    map[dt].push(f);
  });
  const orderedKeys = [...order.filter(k => !!map[k]), ...Object.keys(map).filter(k => !order.includes(k))];
  return orderedKeys.map(k => ({ key: k, items: map[k] }));
};

  // Fetch documents when a case is selected
  useEffect(() => {
    if (!selectedCase) return;

    const fetchDocuments = async () => {
      try {
        setLoadingDocuments(true);
        setAuditResult(null); // Clear previous results
        setError(null);
        const allDocs = await apiClient.getDocumentsForCase(selectedCase);
        console.log('Fetched documents:', allDocs);
        
        // Filter for only the document types we are auditing for now
        const relevantDocs = allDocs.filter((doc: Document) =>
          ['passport'].includes(doc.documentType?doc.documentType:'')
        );
        console.log('Relevant documents for audit:', relevantDocs);
        
        setDocuments(relevantDocs);
      } catch (err) {
        setError('Failed to fetch documents for the selected case.');
      } finally {
        setLoadingDocuments(false);
      }
    };
    fetchDocuments();

    const fetchSubmissionsForCase = async () => {
      try {
        setLoadingSubmissions(true);
        setSubmissions([]);
        // find case object to get client id / email
        const caseObj: any = cases.find(c => c.id === selectedCase);
        const clientId = caseObj?.client?.[0]?.id || caseObj?.client_id || caseObj?.clientId || null;
        const clientEmail = caseObj?.client?.[0]?.email || caseObj?.client_email || null;
        if (!clientId && !clientEmail) {
          console.log('No client information available on case to fetch submissions');
          setLoadingSubmissions(false);
          return;
        }
        const resp = await apiClient.getIntakeFormSubmissions(); // backend: backend/src/controllers/intakeFormController.ts -> getSubmissions
        const allSubs: any[] = Array.isArray(resp) ? resp : (resp?.data || []);
        const filtered = allSubs.filter(s => {
          if (!s) return false;
          if (clientId && s.client_id === clientId) return true;
          if (clientEmail && (s.client_email === clientEmail || s.clientEmail === clientEmail)) return true;
          // fallback: match by client name
          const caseClientName = (caseObj?.client?.[0]?.first_name || caseObj?.clientName || '') + ' ' + (caseObj?.client?.[0]?.last_name || '');
          if (s.client_name && caseClientName.trim() && s.client_name.trim() === caseClientName.trim()) return true;
          return false;
        });
        setSubmissions(filtered);
      } catch (err) {
        console.error('Failed to load intake submissions:', err);
      } finally {
        setLoadingSubmissions(false);
      }
    };
  fetchSubmissionsForCase();
  }, [selectedCase, cases]);

   // NEW: view submission details
  const viewSubmission = async (submissionId: string) => {
    console.log('Viewing submission:', submissionId);
    
    try {
      setSelectedSubmission(null);
      setSubmissionModalOpen(true);
      console.log('Fetching details for submission:', submissionId);
      
      const details = await apiClient.getSubmissionDetails(submissionId);
      console.log('Fetched submission details:', details);
      
      setSelectedSubmission(details);
    } catch (err) {
      console.error('Failed to fetch submission details', err);
      setSelectedSubmission({ error: 'Failed to load details' });
    }
  };

  const handleRunAudit = async () => {
    if (!selectedCase) return;
    setAuditing(true);
    setError(null);
    setAuditResult(null);
     try {
      const resp = await apiClient.runAudit(selectedCase);
      // apiClient.request returns parsed JSON (success, totals, findings)
      if (resp && resp.success) {
        setAuditResult({ totals: resp.totals, findings: resp.findings });
      } else {
        setError(resp?.error || 'Audit failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred during the audit.');
    } finally {
      setAuditing(false);
    }
  };

   const getStatusIcon = (status: string) => {
    switch (status) {
      case 'match': return React.createElement(FiCheckCircle as any, { style: { color: '#10b981' } });
      case 'near_miss': return React.createElement(FiAlertTriangle as any, { style: { color: '#f59e0b' } });
      case 'mismatch': return React.createElement(FiXCircle as any, { style: { color: '#ef4444' } });
      default: return React.createElement(FiMinusCircle as any, { style: { color: '#6b7280' } });
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#1f2937' }}>Audit Documents</h1>
        <select
          value={selectedCase || ''}
          onChange={(e) => setSelectedCase(e.target.value)}
          disabled={loadingCases || auditing}
          style={{ padding: '10px 16px', minWidth: 300, borderRadius: '12px', border: '1px solid #d1d5db' }}
        >
          {loadingCases ? <option>Loading cases...</option> : cases.map((c) => (
            <option key={c.id} value={c.id}>
              {c.title} - ({c.clientName})
            </option>
          ))}
        </select>
      </div>

      {selectedCase && (
        <>
          <div style={{ background: '#f9fafb', padding: 20, borderRadius: 12, marginBottom: 24 }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Documents to be Audited</h3>
            {loadingDocuments ? <p>Loading documents...</p> : (
              documents.length > 0 ? (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {documents.map(doc => (
                    <li key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid #e5e7eb' }}>
                      {React.createElement(FiFile as any, { style: { color: '#8b5cf6' } })}
                      <span>{doc.fileName}</span>
                      <span style={{ marginLeft: 'auto', background: '#eef2ff', color: '#4f46e5', padding: '2px 8px', borderRadius: 99, fontSize: 12 }}>{doc.documentType}</span>
                    </li>
                  ))}
                </ul>
              ) : <p style={{ color: '#6b7280' }}>No passport or marriage certificate documents found for this case.</p>
            )}
          </div>
          <div style={{ background: '#f9fafb', padding: 20, borderRadius: 12, marginBottom: 24 }}>
        <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Intake Submissions for this Client</h3>
        {loadingSubmissions ? <p>Loading submissions...</p> : (
          submissions.length > 0 ? (
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {submissions.map(s => (
                <li key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid #fde68a' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{s.client_name || s.client_email || 'Unnamed submission'}</div>
                    <div style={{ fontSize: 13, color: '#6b7280' }}>{s.intake_forms?.title || s.form_id || 'Intake Form'} • {new Date(s.submitted_at || s.submittedAt || s.created_at || Date.now()).toLocaleString()}</div>
                  </div>
                  <div>
                    <button onClick={() => viewSubmission(s.id)} style={{ padding: '8px 12px', borderRadius: 8, background: '#f59e0b', color: 'white', border: 'none', cursor: 'pointer' }}>View</button>
                  </div>
                </li>
              ))}
            </ul>
          ) : <p style={{ color: '#92400e' }}>No intake submissions found for this client.</p>
        )}
      </div>
 
          <button onClick={handleRunAudit} disabled={auditing || loadingDocuments || documents.length === 0} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 24px', cursor: 'pointer', background: '#8b5cf6', color: 'white', border: 'none', borderRadius: 8, fontSize: 16, fontWeight: 600, opacity: (auditing || documents.length === 0) ? 0.6 : 1 }}>
            {auditing ? React.createElement(FiLoader as any, { className: "animate-spin" }) : React.createElement(FiPlayCircle as any)}
            {auditing ? 'Auditing...' : 'Start Audit'}
          </button>

          {auditing && (
            <div style={{ marginTop: 20, textAlign: 'center', background: '#f9fafb', padding: '20px', borderRadius: '12px', border: '1px solid #f3f4f6' }}>
              <AuditAnimation />
              <div style={{ color: '#4b5563', fontSize: 15, marginTop: 10, fontWeight: 500 }}>
              </div>
              <div style={{ color: '#6b7280', fontSize: 13, marginTop: 4 }}>
                Please wait while the AI assistant reviews the case files...
              </div>
            </div>
          )}
        </>
      )}
      

      {error && <p style={{ color: 'red', marginTop: 20 }}>{React.createElement(FiAlertTriangle as any, { style: { marginRight: 8 } })}{error}</p>}

{auditResult && (
  <div style={{ marginTop: 30 }}>
    <h3 style={{ fontSize: 20, fontWeight: 600, marginBottom: 12 }}>Audit Findings</h3>
    <div style={{ display: 'flex', gap: 24, marginBottom: 18, fontSize: 14 }}>
      <span>Total Compared: <strong>{auditResult.totals.compared}</strong></span>
    </div>

    {groupFindingsByDocument(auditResult.findings).map(group => (
      <div key={group.key} style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <div style={{ background: '#eef2ff', color: '#4f46e5', padding: '6px 10px', borderRadius: 8, fontWeight: 700, textTransform: 'capitalize' }}>
             {group.key === 'passport' ? 'Passport' : group.key}
          </div>
          <div style={{ color: '#6b7280', fontSize: 13 }}>{group.items.length} checked</div>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f3f4f6' }}>
              <th style={{ padding: 12, border: '1px solid #e5e7eb', textAlign: 'left' }}>Field</th>
              <th style={{ padding: 12, border: '1px solid #e5e7eb', textAlign: 'left' }}>Intake Value</th>
              <th style={{ padding: 12, border: '1px solid #e5e7eb', textAlign: 'left' }}>OCR Value</th>
              <th style={{ padding: 12, border: '1px solid #e5e7eb', textAlign: 'left' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {group.items.map((finding, idx) => (
              <tr key={idx} style={{ background: idx % 2 === 0 ? 'white' : '#f9fafb' }}>
                <td style={{ padding: 12, border: '1px solid #e5e7eb' }}>{finding.field}</td>
                <td style={{ padding: 12, border: '1px solid #e5e7eb' }}>{finding.intakeValue ?? '—'}</td>
                <td style={{ padding: 12, border: '1px solid #e5e7eb' }}>{finding.ocrValue ?? '—'}</td>
                <td style={{ padding: 12, border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 8 }}>
                  {getStatusIcon(finding.status)}
                  <span style={{ textTransform: 'capitalize' }}>{finding.status}{finding.readibility ? ` • ${finding.readibility}` : ''}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ))}
  </div>
)}

       {submissionModalOpen && (
  <SubmissionModal
    isOpen={submissionModalOpen}
    submission={selectedSubmission}
    loading={selectedSubmission === null}
    onClose={() => { setSubmissionModalOpen(false); setSelectedSubmission(null); }}
  />
)}
    </div>
  );
}